import { Box, Button, ButtonProps, Flex, HStack } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { Fragment } from "react"
import IconButton, { IconButtonProps, Indicator } from "../button/IconButton"
import { DarkMode } from "../chakra/ui/color-mode"
import Link, { LinkProps } from "../link/Link"
import Tooltip from "../tooltip/Tooltip"
import type {
  AugmentationToolbarButtonProps,
  AugmentationToolbarOption,
  RenderIcon,
  ToolbarContext
} from "./augmentationToolbarTypes"
import { useOptionLabel } from "./useAugmentationToolbar"

// Prune out props that would cause React warnings if passed to a DOM element.
function getOptionButtonProps(option: AugmentationToolbarOption) {
  const {
    key,
    icon,
    renderIcon,
    compositeKey,
    label,
    tLabel,
    indicator,
    isPrimary,
    isVisible,
    showInDetailedMenu,
    ToolbarComponent,
    AugmentationComponent,
    AugmentationNavComponent,
    ...rest
  } = option

  return rest
}

function handleRenderIcon(
  icon: IconName,
  context: ToolbarContext,
  renderIcon?: RenderIcon
) {
  const iconElement = <Pathicon icon={icon} size={18} />
  return renderIcon ? renderIcon(iconElement, context) : iconElement
}

export function AugmentationToolbarButton({
  option,
  isSelected,
  detailed
}: AugmentationToolbarButtonProps) {
  const { icon, renderIcon, indicator, to, relativeTo, href, isPrimary } =
    option

  const label = useOptionLabel(option)

  const linkProps: LinkProps = {
    to,
    relativeTo,
    href,
    variant: "plain",
    textDecor: "none"
  }

  // Compact island buttons (non-detailed, non-primary). Stated on the button
  // rather than a shared CSS bag — this is the toolbar's own chrome.
  const useIslandChrome = !detailed && !isPrimary
  const islandButtonProps: Partial<IconButtonProps> = useIslandChrome
    ? {
        bg: isSelected ? "rgba(0, 0, 0, 0.6)" : "transparent",
        borderRadius: "augmentationPanelField",
        color: isSelected ? "#fff" : "rgba(0, 0, 0, 0.6)",
        flexShrink: 0,
        size: "sm",
        variant: "ghost",
        _hover: isSelected
          ? {
              bg: "rgba(0, 0, 0, 0.6)",
              color: "#fff"
            }
          : {
              bg: "augmentationPanel.fill.muted",
              color: "rgba(0, 0, 0, 0.8)"
            }
      }
    : {}

  const buttonProps: ButtonProps = {
    size: "sm",
    color: "blackAlpha.600",
    colorPalette: "blackAlpha",
    variant: isSelected ? "subtle" : "ghost",
    _hover: {
      color: "blackAlpha.900",
      bg: "whiteAlpha.600"
    },
    ...getOptionButtonProps(option)
  }

  // Primary action styling — larger, branded, elevated.
  const primaryProps: Partial<IconButtonProps> = isPrimary
    ? {
        roundSize: 12,
        variant: "solid",
        colorPalette: "brand",
        color: "brand.500",
        // Even, circular shadow. `lg` is a stacked rect shadow and reads as a
        // box when any ancestor clips overflow.
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.22)",
        _hover: {
          color: "brand.600",
          bg: "white"
        }
      }
    : {}

  const IconButtonWrapper = isPrimary ? DarkMode : Fragment

  return detailed ? (
    <Button
      asChild
      className="AugmentationToolbarButton"
      data-selected={isSelected ? "" : undefined}
      w="100%"
      justifyContent="flex-start"
      {...buttonProps}
    >
      <Link {...linkProps}>
        <HStack alignItems="center">
          <Flex pos="relative">
            {handleRenderIcon(icon, "toolbar", renderIcon)}
            {indicator && <Indicator top={0} right={0} />}
          </Flex>
          <span>{label}</span>
        </HStack>
      </Link>
    </Button>
  ) : (
    <IconButtonWrapper>
      <IconButton
        asChild
        aria-label={label}
        className="AugmentationToolbarButton"
        data-selected={isSelected ? "" : undefined}
        // Island buttons use borderRadius on the props below; roundSize would
        // force a full circle and fight the 10px field radius.
        roundSize={useIslandChrome ? undefined : 10}
        indicator={indicator}
        {...buttonProps}
        {...primaryProps}
        {...islandButtonProps}
      >
        <Link {...linkProps}>
          {handleRenderIcon(icon, "toolbar", renderIcon)}
        </Link>
      </IconButton>
    </IconButtonWrapper>
  )
}

export function AugmentationToolbarItem({
  option,
  isSelected,
  detailed
}: {
  option: AugmentationToolbarOption
  isSelected: boolean
  detailed?: boolean
}) {
  const {
    // Pull out the toolbar option props to avoid React attribute warnings.
    children,
    compositeKey,
    ToolbarComponent,
    AugmentationComponent,
    AugmentationNavComponent,
    ...prunedOption
  } = option
  const ToolbarOptionComponent = ToolbarComponent || AugmentationToolbarButton

  const label = useOptionLabel(option)

  return (
    <Tooltip
      key={prunedOption.key}
      // No tooltip when detailed since we show the label.
      title={detailed ? undefined : label}
      fitContent
      popover={{
        positioning: { placement: "top-start" },
        openDelay: 1000
      }}
    >
      {/* Wrapper for ChakraTooltip Popover.Trigger to pass ref to. */}
      <Box w={detailed ? "100%" : "auto"}>
        <ToolbarOptionComponent
          option={prunedOption}
          isSelected={isSelected}
          detailed={detailed}
        />
      </Box>
    </Tooltip>
  )
}
